import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

/**
 * ReferralPanel — referral link, stats (invited, active, free months earned),
 * copy + WhatsApp share, list of filleuls. Self-contained, fetches on mount.
 */
export default function ReferralPanel({ slug, businessName }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/dashboard/referrals/${slug}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [slug]);

  if (!data || !data.code) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/parrainage/${data.code}`;
  const shareText = `Rejoignez Mon Commerce 68 — l'annuaire premium du Haut-Rhin. Je vous invite avec ${data.code} : ${shareUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copie impossible — sélectionnez manuellement le lien");
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 mt-8" data-testid="referral-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 shrink-0 bg-secondary/15 text-secondary rounded-sm grid place-items-center">
          <Sparkle size={18} weight="duotone" />
        </div>
        <div>
          <div className="label-uppercase mb-0.5">Programme de parrainage</div>
          <div className="font-display text-xl font-medium leading-tight">Offrez le 68, gagnez des mois gratuits</div>
          <div className="text-xs text-muted-foreground mt-1">Chaque commerçant qui passe Premium grâce à vous vous offre <b>1 mois gratuit</b>. Cumulable, non-plafonné.</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="referral-stats">
        <ReferralStat label="Parrainés" value={data.referred?.length || 0} testid="referral-stat-total" />
        <ReferralStat label="Actifs Premium" value={data.active_referrals || 0} color="text-primary" testid="referral-stat-active" />
        <ReferralStat label="Jours offerts" value={data.total_days_granted || 0} color="text-secondary" testid="referral-stat-credit" />
      </div>

      {data.total_days_granted > 0 && data.current_premium_expires_at && (
        <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 mb-4 text-sm" data-testid="referral-reward-summary">
          <span className="font-semibold text-primary">🎉 {data.total_days_granted} jours offerts</span>
          <span className="text-muted-foreground"> · Votre Premium est actif jusqu'au <b className="text-foreground">{new Date(data.current_premium_expires_at).toLocaleDateString("fr-FR")}</b>.</span>
        </div>
      )}

      <div className="bg-accent/30 border border-border rounded-sm p-4 mb-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Votre lien de parrainage</div>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <input readOnly value={shareUrl} className="flex-1 min-w-0 bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono outline-none" data-testid="referral-link-input" />
          <button onClick={copy} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-4 py-2 rounded-sm text-xs font-semibold whitespace-nowrap" data-testid="referral-copy-btn">
            {copied ? "✓ Copié" : "Copier"}
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
            target="_blank" rel="noreferrer"
            className="bg-[#25D366] text-white hover:bg-[#1fb855] px-4 py-2 rounded-sm text-xs font-semibold whitespace-nowrap"
            data-testid="referral-whatsapp-btn"
          >
            WhatsApp
          </a>
        </div>
        <div className="text-[10px] text-muted-foreground mt-2">Code : <b className="text-foreground tabular-nums">{data.code}</b></div>
      </div>

      {data.referred && data.referred.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Vos filleuls</div>
          <ul className="divide-y divide-border border border-border rounded-sm">
            {data.referred.map((r) => {
              const isActive = (r.premium?.tier === "premium" || r.premium?.tier === "premium_ai");
              return (
                <li key={r.slug} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`referral-row-${r.slug}`}>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.city}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${isActive ? "bg-primary/10 text-primary" : "bg-accent text-muted-foreground"}`}>
                    {isActive ? "Premium" : "En attente"}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(!data.referred || data.referred.length === 0) && (
        <div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-4 text-center" data-testid="referral-empty">
          Partagez votre lien pour inviter vos confrères commerçants de {businessName?.split(" ")[0] || "votre ville"}. Chaque activation Premium = 1 mois offert sur votre abonnement.
        </div>
      )}
    </div>
  );
}

function ReferralStat({ label, value, color, testid }) {
  return (
    <div className="bg-accent/20 border border-border rounded-sm p-3 text-center" data-testid={testid}>
      <div className={`font-display text-2xl tabular-nums font-medium ${color || ""}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
